import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Box,
  Divider,
  SkeletonDisplayText,
  SkeletonBodyText,
  Banner,
} from '@shopify/polaris';
import type { DashboardStats, GoogleConnectionStatus } from '../types/api';

export interface TodaysPingsCardProps {
  /** Dashboard stats data */
  stats: DashboardStats | null;
  /** Google connection status */
  googleStatus: GoogleConnectionStatus | null;
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if fetch failed */
  error?: string | null;
}

/**
 * Calculate success rate percentage
 */
function calculateSuccessRate(
  successful: number,
  failed: number
): number {
  const total = successful + failed;
  if (total === 0) return 100;
  return Math.round((successful / total) * 100 * 10) / 10; // Round to 1 decimal
}

/**
 * Get badge tone based on success rate
 */
function getSuccessRateTone(rate: number): 'success' | 'warning' | 'critical' {
  if (rate >= 95) return 'success';
  if (rate >= 80) return 'warning';
  return 'critical';
}

/**
 * Loading skeleton for TodaysPingsCard
 */
function TodaysPingsCardSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonBodyText lines={1} />
        <InlineStack gap="400" align="space-between" wrap={false}>
          <Box minWidth="120px">
            <BlockStack gap="200">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={1} />
            </BlockStack>
          </Box>
          <Box minWidth="120px">
            <BlockStack gap="200">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={1} />
            </BlockStack>
          </Box>
        </InlineStack>
        <Divider />
        <SkeletonBodyText lines={1} />
      </BlockStack>
    </Card>
  );
}

/**
 * Card displaying today's ping counts for IndexNow and Google.
 *
 * Features:
 * - Shows IndexNow pings sent today
 * - Shows Google pings sent today (if connected)
 * - Displays success rate percentage
 * - Shows Google quota usage when connected
 * - Loading skeleton state
 * - Error banner for fetch failures
 *
 * @example
 * ```tsx
 * <TodaysPingsCard
 *   stats={dashboardStats}
 *   googleStatus={googleStatus}
 *   loading={isLoading}
 *   error={errorMessage}
 * />
 * ```
 */
export function TodaysPingsCard({
  stats,
  googleStatus,
  loading = false,
  error = null,
}: TodaysPingsCardProps) {
  if (loading) {
    return <TodaysPingsCardSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Today's Pings
          </Text>
          <Banner tone="critical">
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Today's Pings
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            No data available
          </Text>
        </BlockStack>
      </Card>
    );
  }

  const { today } = stats;
  const totalSuccessful = today.indexnowPings + today.googlePings;
  const successRate = calculateSuccessRate(totalSuccessful, today.failedPings);
  const successRateTone = getSuccessRateTone(successRate);

  const isGoogleConnected = googleStatus?.connected ?? false;
  const googleQuotaUsed = googleStatus?.quotaUsed ?? 0;
  const googleQuotaLimit = googleStatus?.quotaLimit ?? 200;

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h3" variant="headingSm" tone="subdued">
          Today's Pings
        </Text>

        {/* Main stats row - responsive layout */}
        <Box>
          <InlineStack gap="600" align="start" wrap>
            {/* IndexNow pings */}
            <Box minWidth="100px">
              <BlockStack gap="100">
                <Text as="p" variant="headingXl" fontWeight="bold">
                  {today.indexnowPings}
                </Text>
                <InlineStack gap="100" blockAlign="center">
                  <Badge tone="info">IndexNow</Badge>
                </InlineStack>
              </BlockStack>
            </Box>

            {/* Google pings */}
            <Box minWidth="100px">
              <BlockStack gap="100">
                <Text as="p" variant="headingXl" fontWeight="bold">
                  {today.googlePings}
                </Text>
                <InlineStack gap="100" blockAlign="center">
                  <Badge tone={isGoogleConnected ? 'success' : undefined}>
                    Google
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Box>

            {/* Total pings */}
            <Box minWidth="100px">
              <BlockStack gap="100">
                <Text as="p" variant="headingXl" fontWeight="bold">
                  {today.totalPings}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Total
                </Text>
              </BlockStack>
            </Box>
          </InlineStack>
        </Box>

        <Divider />

        {/* Success rate and quota info */}
        <InlineStack gap="400" align="space-between" wrap>
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              Success rate:
            </Text>
            <Badge tone={successRateTone}>
              {`${successRate}%`}
            </Badge>
          </InlineStack>

          {isGoogleConnected && (
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" variant="bodySm" tone="subdued">
                Google quota:
              </Text>
              <Text as="span" variant="bodySm">
                {googleQuotaUsed} / {googleQuotaLimit}
              </Text>
            </InlineStack>
          )}
        </InlineStack>

        {/* Google not connected notice */}
        {!isGoogleConnected && (
          <Banner tone="info">
            Connect Google to ping Google's Indexing API too.
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
